import { getAPIPath } from '@interhyp/platform-request';
import { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import { jsx, Fragment } from 'react/jsx-runtime';
import { useHistory, useLocation } from '@interhyp/platform-router';
import { v4 } from 'uuid';

const authenticationContext = /*#__PURE__*/createContext(undefined);

function getSessionClient() {
  return axios.create({
    baseURL: getAPIPath('platform-frontend-session'),
    validateStatus: null
  });
}

function httpGetSession() {
  const client = getSessionClient();
  return client.get('/v1/session');
}

function getCookie(cookieString, name) {
  const cookies = cookieString.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue;
    }
  }
}

function getExpiration() {
  const cookieString = document.cookie;
  const expirationTime = getCookie(cookieString, 'H_EXP');
  if (typeof expirationTime === 'undefined') {
    return;
  }
  return Number.parseInt(expirationTime);
}

function useExistingSession(login) {
  const [isDone, setIsDone] = useState(false);
  useEffect(() => {
    async function getSession() {
      const exp = getExpiration();
      if (typeof exp !== 'undefined') {
        const response = await httpGetSession();
        if (response.status === 200) {
          login(response.data);
        } else if (response.status === 206) {
          login(response.data, true);
        }
      }
      setIsDone(true);
    }
    void getSession();
  }, [login]);
  return isDone;
}

function useExpirationFocus(logout, enabled = true) {
  useEffect(() => {
    function handleFocus() {
      const exp = getExpiration();
      if (exp) {
        // Compare expire time with current time
        const remainingTime = exp - Date.now();
        if (remainingTime < 0) {
          logout();
        }
      } else {
        // If there is no expire cookie anymore, force a logout (only the local state without backend call)
        logout(true);
      }
    }
    if (enabled) {
      window.addEventListener('focus', handleFocus);
    }
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, logout]);
}

function useExpirationTimeout(logout, enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const exp = getExpiration();
    if (typeof exp === 'undefined') {
      // Directly logout because no expiration defined (if the cookie is lost, a re-login should fix it)
      logout(true);
    } else {
      const remainingTime = exp - Date.now();
      const timer = setTimeout(logout, remainingTime);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [enabled, logout]);
}

async function httpDeleteSession() {
  const client = getSessionClient();
  return client.delete('/v1/session');
}

function AuthenticationProvider({
  children,
  identity
}) {
  const [state, setState] = useState({
    isAuthenticated: !!identity,
    identity,
    isMfaRequired: false
  });
  const login = useCallback((identity, mfaRequired) => {
    setState({
      identity,
      isAuthenticated: !mfaRequired,
      isMfaRequired: !!mfaRequired
    });
  }, []);
  const logout = useCallback(async (stateOnly = false, redirectUri = window.location.pathname, doRedirectLogout = false) => {
    let deleteSessionPromise = new Promise(() => {
    });
    if (!stateOnly) {
      if (doRedirectLogout) {
        const currentOrigin = location.origin;
        const redirectUrl = new URL(redirectUri, currentOrigin);
        const serviceUrl = `${getAPIPath('platform-frontend-session')}/v1/session/logout`;
        const url = new URL(serviceUrl, currentOrigin);
        url.searchParams.append('redirectUrl', redirectUrl.href);
        window.location.href = url.href;

        // no state change needed when there is a redirect
        return;
      }
      deleteSessionPromise = httpDeleteSession().catch(() => void 0);
    }
    setState({
      identity: undefined,
      isAuthenticated: false,
      isMfaRequired: false
    });
    return deleteSessionPromise;
  }, []);
  const completeMfa = useCallback(identity => {
    setState(state => ({
      ...state,
      identity: identity ? identity : state.identity,
      isAuthenticated: true,
      isMfaRequired: false
    }));
  }, []);
  const isDone = useExistingSession(login);

  // Only enable logout hooks if the identity is not mocked
  useExpirationTimeout(logout, !identity);
  useExpirationFocus(logout, !identity);
  const value = useMemo(() => {
    return {
      state,
      actions: {
        login,
        logout,
        completeMfa
      }
    };
  }, [login, logout, state, completeMfa]);
  if (!isDone) {
    return null;
  }
  return /*#__PURE__*/jsx(authenticationContext.Provider, {
    value: value,
    children: children
  });
}

function useLogout() {
  const context = useContext(authenticationContext);
  if (!context) {
    throw new Error('Expected AuthenticationProvider provider to exist.');
  }
  return context.actions.logout;
}

function useLogin() {
  const context = useContext(authenticationContext);
  if (!context) {
    throw new Error('Expected AuthenticationProvider provider to exist.');
  }
  return context.actions.login;
}

function httpCreateSessionFromPassword(credentials, captcha) {
  const client = getSessionClient();
  return client.request({
    method: 'POST',
    url: '/v1/session/password',
    headers: {
      'Content-Type': 'application/json',
      'X-Captcha': captcha
    },
    data: JSON.stringify(credentials)
  });
}

function useLoginWithPassword() {
  const login = useLogin();
  return useCallback(async (credentials, captcha) => {
    const response = await httpCreateSessionFromPassword(credentials, captcha);
    if (response.status !== 200) {
      return {
        success: false
      };
    }
    login(response.data, response.headers.location === '/login/mfa' ? true : undefined);
    return {
      success: true,
      redirect: response.headers.location
    };
  }, [login]);
}

/**
 * Create login for user
 * @param keepParams - flag to keep the old params existing (except client param)
 */
function useLoginWithSSO(keepParams = false) {
  const {
    push
  } = useHistory();
  const {
    pathname,
    search
  } = useLocation();
  const oldParams = keepParams ? search : undefined;
  return useCallback(client => {
    const adaptedSearch = new URLSearchParams(oldParams);
    adaptedSearch.set('client', client);
    push({
      pathname,
      search: `?${adaptedSearch.toString()}`
    });
  }, [pathname, push, oldParams]);
}

function useCompleteMfa() {
  const context = useContext(authenticationContext);
  if (!context) {
    throw new Error('Expected AuthenticationProvider provider to exist.');
  }
  return context.actions.completeMfa;
}

function useIsAuthenticated() {
  const context = useContext(authenticationContext);
  if (!context) {
    return false;
  }
  return context.state.isAuthenticated;
}

function useIsMfaRequired() {
  const context = useContext(authenticationContext);
  if (!context) {
    return false;
  }
  return context.state.isMfaRequired;
}

function useIdentity() {
  const context = useContext(authenticationContext);
  if (!context?.state.isAuthenticated) {
    return undefined;
  }
  return context?.state.identity;
}

async function httpCreateSessionFromBroker() {
  const client = getSessionClient();
  return client.post('/v1/session/broker');
}

function useParam(name) {
  const {
    search
  } = useLocation();
  const qs = new URLSearchParams(search);
  if (qs.has(name)) {
    return qs.get(name);
  }
}

const PARAMS_TO_REMOVE = ['client', 'code', 'state', 'session_state', 'iss', 'username', 'password'];
function sanitizeSearch(search) {
  if (!search) {
    return search;
  }
  const qs = new URLSearchParams(search);
  PARAMS_TO_REMOVE.forEach(name => {
    qs.delete(name);
  });
  return `?${qs.toString()}`;
}

function useRemoveParams() {
  const {
    search
  } = useLocation();
  const {
    replace
  } = useHistory();
  return useCallback(() => {
    const sanitisedSearch = sanitizeSearch(search);
    replace({
      search: sanitisedSearch
    });
  }, [replace, search]);
}

function useBrokerSSO() {
  const client = useParam('client');
  const removeParams = useRemoveParams();
  const login = useLogin();
  const logout = useLogout();
  useEffect(() => {
    async function initiateSSO() {
      await logout();
      const response = await httpCreateSessionFromBroker();
      if (response.status === 200) {
        login(response.data);
      } else {
        console.error('Broker login failed!');
      }
      removeParams();
    }
    if (client === 'broker') {
      void initiateSSO();
    }
  }, [client, login, logout, removeParams]);
}

function useHref() {
  const {
    pathname
  } = useLocation();
  return `${location.origin}${pathname}`;
}

async function httpCreateSessionFromCode(credentials) {
  const client = getSessionClient();
  return client.request({
    method: 'POST',
    url: '/v1/session/code',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(credentials)
  });
}

function convertIdpToRealm(idp) {
  if (idp === 'interhyp' || idp === 'employee') {
    return 'employee';
  }
  return 'sso';
}

function useCode() {
  const isAuthenticated = useIsAuthenticated();
  const redirectUrl = useHref();
  const code = useParam('code');
  const stateParam = useParam('state');
  const login = useLogin();
  useEffect(() => {
    async function handleLogin() {
      if (!code || !stateParam) {
        return;
      }
      const sessionState = sessionStorage.getItem('sessionState');
      sessionStorage.removeItem('sessionState');
      if (!sessionState) {
        return;
      }
      const {
        state,
        idp,
        redirectUri
      } = JSON.parse(sessionState);
      if (state !== stateParam) {
        return;
      }
      const response = await httpCreateSessionFromCode({
        realm: convertIdpToRealm(idp),
        code,
        redirectUrl: redirectUri
      });
      if (response.status === 200) {
        login(response.data);
      }
    }
    if (!isAuthenticated) {
      void handleLogin();
    }
  }, [isAuthenticated, code, login, redirectUrl, stateParam]);
}

async function httpCreateSessionFromDibaMo(request) {
  const client = getSessionClient();
  return client.request({
    method: 'POST',
    url: '/v1/session/diba-mo',
    headers: {
      'Content-Type': 'application/json'
    },
    data: JSON.stringify(request)
  });
}

function useDibaMoSSO() {
  const client = useParam('client');
  const username = useParam('username');
  const password = useParam('password');
  const removeParams = useRemoveParams();
  const login = useLogin();
  const logout = useLogout();
  useEffect(() => {
    async function initiateSSO() {
      // we are calling logout before the login to invalidate potential old cookies since baufinow does not offer a logout process
      await logout();
      if (!username || !password) {
        return;
      }
      const response = await httpCreateSessionFromDibaMo({
        username,
        password
      });
      if (response.status === 200) {
        login(response.data);
      } else {
        console.error('Diba-MO login failed!');
      }
      // params have to be removed manually since we excluded them in SSO
      removeParams();
    }
    if (client === 'diba-mo') {
      void initiateSSO();
    }
  }, [client, login, username, password, logout, removeParams]);
}

function useSSO() {
  const {
    search
  } = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const client = useParam('client');
  useEffect(() => {
    async function initiateSSO() {
      if (!client || client === 'broker' || client === 'diba-mo') {
        return;
      }
      const currentUri = new URL(location.href);
      currentUri.searchParams.delete('client');
      const redirectUri = currentUri.href;
      const state = v4();
      sessionStorage.setItem('sessionState', JSON.stringify({
        idp: client,
        state,
        redirectUri
      }));
      const sessionPath = `${getAPIPath('platform-frontend-session')}/v1/session/sso`;
      const url = new URL(sessionPath, location.origin);
      url.searchParams.append('idp', client);
      url.searchParams.append('state', state);
      url.searchParams.append('redirectUri', redirectUri);
      location.href = url.toString();
    }
    if (!isAuthenticated) {
      void initiateSSO();
    }
  }, [isAuthenticated, client, search]);
}

function useHasParams(params) {
  const {
    search
  } = useLocation();
  return useMemo(() => {
    const qs = new URLSearchParams(search);
    return params.some(p => qs.has(p));
  }, [params, search]);
}

function SSO({
  children
}) {
  const isAuthenticated = useIsAuthenticated();
  const isLoggingIn = useHasParams(['code', 'state', 'client']);
  const client = useParam('client');
  const removeParams = useRemoveParams();
  useDibaMoSSO();
  useBrokerSSO();
  useSSO();
  useCode();
  useEffect(() => {
    // exclude diba-mo and broker client because we always want to have a fresh login und thus remove params manually after login process
    if (isAuthenticated && client !== 'diba-mo' && client !== 'broker') {
      removeParams();
    }
  }, [isAuthenticated, removeParams, client]);
  if (isLoggingIn) {
    return null;
  }
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
}

const LOGIN_URL = '/login';

function RedirectToLogin({
  fallbackContent
}) {
  const {
    pathname,
    search
  } = useLocation();
  const {
    replace
  } = useHistory();
  const searchParams = new URLSearchParams(search);
  searchParams.append('targetUrl', pathname);
  if (pathname === LOGIN_URL) {
    return /*#__PURE__*/jsx(Fragment, {
      children: fallbackContent
    });
  }
  replace({
    pathname: LOGIN_URL,
    search: searchParams.toString()
  });
  window.location.reload();
  return null;
}

/**
 * @deprecated
 * The AuthenticationProviderBridge is only for supporting the new platform-auth package
 * in legacy applications and will be removed as soon as the applications are using
 * the new package.
 */
function AuthenticationProviderBridge({
  children,
  identity
}) {
  const login = useCallback(() => void 0, []);
  const logout = useCallback(async () => void 0, []);
  const completeMfa = useCallback(() => void 0, []);
  const value = useMemo(() => {
    return {
      state: {
        isAuthenticated: !!identity,
        identity,
        isMfaRequired: false
      },
      actions: {
        login,
        logout,
        completeMfa
      }
    };
  }, [login, logout, identity, completeMfa]);
  return /*#__PURE__*/jsx(authenticationContext.Provider, {
    value: value,
    children: children
  });
}

const Authentication = AuthenticationProvider;

export { Authentication, AuthenticationProvider, AuthenticationProviderBridge, RedirectToLogin, SSO, useCompleteMfa, useIdentity, useIsAuthenticated, useIsMfaRequired, useLogin, useLoginWithPassword, useLoginWithSSO, useLogout };
//# sourceMappingURL=index.js.map
